
import { useEffect } from 'react';
import Header from '../../home/components/Header';
import Footer from '../../home/components/Footer';

const UnderstandingRuralSpacesAfricaPage = () => {
  useEffect(() => {
    document.title = 'Understanding Rural Spaces in Africa - Research Report | TS Dotyeni Investments';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Deep dive into the unique characteristics and challenges of rural African communities and markets. Comprehensive analysis of rural development opportunities in Africa.');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative h-96 bg-gradient-to-r from-emerald-900 to-emerald-700 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/d8658de340e887fc3b6fe8483b6ac47f.jpg')`
          }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div className="text-white max-w-3xl">
            <h1 className="text-5xl font-bold mb-6">Understanding Rural Spaces in Africa</h1>
            <p className="text-xl leading-relaxed">
              Deep dive into the unique characteristics and challenges of rural African communities and markets
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Executive Summary */}
        <section className="mb-12">
          <div className="bg-emerald-50 rounded-2xl p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Executive Summary</h2>
            <p className="text-gray-700 leading-relaxed">
              Rural Africa represents 60% of the continent's population, yet remains largely underserved by modern infrastructure and services. This comprehensive analysis examines the unique characteristics, challenges, and opportunities within rural African communities, providing essential insights for investors and development organizations seeking to create sustainable impact while generating returns.
            </p>
          </div>
        </section>

        {/* Key Findings */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Key Findings</h2>
          
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-community-line text-emerald-600 text-xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Population Dynamics</h3>
              <p className="text-gray-600">Over 400 million people live in rural Africa, with 70% under age 30, creating a massive demographic dividend opportunity.</p>
            </div>
            
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-money-dollar-circle-line text-emerald-600 text-xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Economic Potential</h3>
              <p className="text-gray-600">Rural economies contribute $180 billion annually to Africa's GDP, with untapped potential exceeding $500 billion.</p>
            </div>
            
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-smartphone-line text-emerald-600 text-xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Digital Connectivity</h3>
              <p className="text-gray-600">Mobile penetration reaches 85% in rural areas, enabling leapfrog opportunities in financial services and e-commerce.</p>
            </div>
            
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-seedling-line text-emerald-600 text-xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Agricultural Dominance</h3>
              <p className="text-gray-600">Agriculture employs 65% of rural populations, with modernization opportunities worth $35 billion annually.</p>
            </div>
          </div>
        </section>

        {/* Detailed Analysis */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Detailed Analysis</h2>
          
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Social Infrastructure Gaps</h3>
              <div className="bg-gray-50 rounded-xl p-6 mb-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=Rural%20African%20community%20with%20basic%20infrastructure%20needs%2C%20schools%20and%20healthcare%20facilities%2C%20water%20access%20points%2C%20community%20development%20projects%2C%20social%20infrastructure%20development&width=800&height=300&seq=social-infra&orientation=landscape"
                  alt="Rural African social infrastructure"
                  className="w-full h-48 object-cover object-top rounded-lg mb-4"
                />
                <p className="text-gray-700 leading-relaxed">
                  Rural communities face significant infrastructure deficits: 40% lack access to clean water, 60% have no electricity, and 70% are more than 10km from the nearest health facility. However, these gaps represent massive investment opportunities, with government and international funding increasingly available for infrastructure development projects.
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Economic Structures and Opportunities</h3>
              <div className="bg-gray-50 rounded-xl p-6 mb-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=Rural%20African%20market%20economy%2C%20local%20traders%20and%20small%20businesses%2C%20agricultural%20value%20chains%2C%20economic%20development%20opportunities%2C%20community%20commerce%20activities&width=800&height=300&seq=economic-struct&orientation=landscape"
                  alt="Rural African economic structures"
                  className="w-full h-48 object-cover object-top rounded-lg mb-4"
                />
                <p className="text-gray-700 leading-relaxed">
                  Rural economies are characterized by informal markets, subsistence agriculture, and limited access to formal financial services. Only 25% of rural adults have bank accounts, yet mobile money adoption exceeds 60%. This creates opportunities for fintech solutions, agricultural value chain development, and modern retail infrastructure.
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">Cultural and Social Dynamics</h3>
              <div className="bg-gray-50 rounded-xl p-6 mb-6">
                <img 
                  src="https://readdy.ai/api/search-image?query=African%20rural%20community%20cultural%20activities%2C%20traditional%20leadership%20structures%2C%20community%20meetings%20and%20social%20gatherings%2C%20cultural%20preservation%20and%20modern%20development%20balance&width=800&height=300&seq=cultural-dynamics&orientation=landscape"
                  alt="Rural African cultural dynamics"
                  className="w-full h-48 object-cover object-top rounded-lg mb-4"
                />
                <p className="text-gray-700 leading-relaxed">
                  Traditional governance structures remain influential, with 80% of rural land governed by customary law. Community decision-making processes emphasize consensus and collective benefit. Successful development projects must engage traditional leaders and align with cultural values while introducing modern solutions.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Investment Implications */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Investment Implications</h2>
          
          <div className="bg-emerald-50 rounded-2xl p-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">Strategic Recommendations</h3>
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="w-6 h-6 bg-emerald-600 rounded-full flex items-center justify-center mr-4 mt-1">
                  <i className="ri-check-line text-white text-sm"></i>
                </div>
                <div>
                  <strong className="text-gray-900">Community-Centric Approach:</strong>
                  <span className="text-gray-700"> Engage traditional leaders and community structures from project inception to ensure cultural alignment and local buy-in.</span>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-6 h-6 bg-emerald-600 rounded-full flex items-center justify-center mr-4 mt-1">
                  <i className="ri-check-line text-white text-sm"></i>
                </div>
                <div>
                  <strong className="text-gray-900">Technology Leapfrogging:</strong>
                  <span className="text-gray-700"> Leverage mobile technology and digital solutions to bypass traditional infrastructure limitations and reach rural populations efficiently.</span>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-6 h-6 bg-emerald-600 rounded-full flex items-center justify-center mr-4 mt-1">
                  <i className="ri-check-line text-white text-sm"></i>
                </div>
                <div>
                  <strong className="text-gray-900">Value Chain Integration:</strong>
                  <span className="text-gray-700"> Focus on agricultural value addition and processing to capture more value locally while creating employment opportunities.</span>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-6 h-6 bg-emerald-600 rounded-full flex items-center justify-center mr-4 mt-1">
                  <i className="ri-check-line text-white text-sm"></i>
                </div>
                <div>
                  <strong className="text-gray-900">Patient Capital:</strong>
                  <span className="text-gray-700"> Rural investments require longer time horizons but offer sustainable returns and significant social impact potential.</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Market Data */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Market Data & Statistics</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-white border border-gray-200 rounded-xl">
              <div className="text-3xl font-bold text-emerald-600 mb-2">400M+</div>
              <div className="text-gray-600">Rural Population</div>
            </div>
            
            <div className="text-center p-6 bg-white border border-gray-200 rounded-xl">
              <div className="text-3xl font-bold text-emerald-600 mb-2">$180B</div>
              <div className="text-gray-600">Annual GDP Contribution</div>
            </div>
            
            <div className="text-center p-6 bg-white border border-gray-200 rounded-xl">
              <div className="text-3xl font-bold text-emerald-600 mb-2">65%</div>
              <div className="text-gray-600">Agricultural Employment</div>
            </div>
            
            <div className="text-center p-6 bg-white border border-gray-200 rounded-xl">
              <div className="text-3xl font-bold text-emerald-600 mb-2">85%</div>
              <div className="text-gray-600">Mobile Penetration</div>
            </div>
            
            <div className="text-center p-6 bg-white border border-gray-200 rounded-xl">
              <div className="text-3xl font-bold text-emerald-600 mb-2">25%</div>
              <div className="text-gray-600">Banked Population</div>
            </div>
            
            <div className="text-center p-6 bg-white border border-gray-200 rounded-xl">
              <div className="text-3xl font-bold text-emerald-600 mb-2">$500B</div>
              <div className="text-gray-600">Untapped Potential</div>
            </div>
          </div>
        </section>

        {/* Conclusion */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Conclusion</h2>
          <div className="bg-gray-50 rounded-2xl p-8">
            <p className="text-gray-700 leading-relaxed mb-6">
              Rural Africa presents a compelling investment landscape characterized by significant demographic advantages, growing digital connectivity, and substantial infrastructure needs. While challenges exist around traditional governance structures and limited formal financial inclusion, these same factors create opportunities for innovative solutions and sustainable development models.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Successful rural investment strategies must balance respect for cultural values with modern development approaches, leverage technology to overcome infrastructure limitations, and maintain long-term perspectives that align financial returns with community development goals. The potential for transformative impact and sustainable returns makes rural Africa an essential component of any comprehensive African investment strategy.
            </p>
          </div>
        </section>

        {/* Call to Action */}
        <section className="text-center">
          <div className="bg-emerald-600 rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">Ready to Explore Rural Investment Opportunities?</h3>
            <p className="text-emerald-100 mb-6">
              Contact our research team to discuss how these insights can inform your investment strategy.
            </p>
            <button className="bg-white text-emerald-600 px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors duration-200 font-semibold whitespace-nowrap cursor-pointer">
              Schedule Consultation
            </button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default UnderstandingRuralSpacesAfricaPage;
